import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Button, Card, HStack, Text, VStack } from '@chakra-ui/react';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { SessionsLayout } from '@src/components/sco/Advisors/layout/SessionsLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { acceptingSessionsDirectoryHref } from '@src/components/sco/Advisors/utils/directoryFilters';
import { formatUsd, formatWhen, personName } from '@src/components/sco/Advisors/utils/sessionFormat';
import { SESSION_LIST_ACTION_LABEL, sessionListAction, sessionStatusPalette } from '@src/components/sco/Advisors/utils/sessionStatus';
import { parseSessionBucket, SESSION_BUCKET_LABELS, SESSION_BUCKET_STATUS_KEYS, type SessionBucket } from '@src/components/sco/Advisors/utils/sessionsNav';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_SESSIONS } from '@src/gql/queries/AdvisorSessions';
import { ScoAdvisorSession, ScoAdvisorSessionCollection } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { LuCalendarPlus, LuVideo } from 'react-icons/lu';
const PAGE_SIZE = 50;
const BUCKET_EMPTY: Record<SessionBucket, {
  title: string;
  body: string;
  cta?: string;
}> = {
  all: {
    title: 'No sessions yet',
    body: 'Choose an advisor who takes paid sessions and propose times.',
    cta: 'Book a Session'
  },
  action: {
    title: 'Nothing needs you right now',
    body: 'When a counterpart proposes times, those sessions land here.'
  },
  upcoming: {
    title: 'No upcoming sessions',
    body: 'Confirmed and live sessions will show up here so you can join on time.',
    cta: 'Book a Session'
  },
  past: {
    title: 'No past sessions',
    body: 'Completed and canceled sessions appear here, including ratings.'
  }
};
type SessionsQueryData = {
  advisorSessions?: ScoAdvisorSessionCollection | null;
};

/** The other party on the session, from the viewer's perspective. */
const counterpart = (session: ScoAdvisorSession) => {
  const viewingAsExpert = session.currentUserRole === 'EXPERT';
  return personName(viewingAsExpert ? session.seekerUser : session.expertUser, viewingAsExpert ? 'Founder' : 'Advisor');
};
export const SessionsListSection = () => {
  const $ = _c(36);
  const paths = useAdvisorsPaths();
  const router = useRouter();
  let t0;
  if ($[0] !== router.query.bucket) {
    t0 = parseSessionBucket(router.query.bucket);
    $[0] = router.query.bucket;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const bucket = t0;
  const t1 = SESSION_BUCKET_STATUS_KEYS[bucket];
  let t2;
  if ($[2] !== t1) {
    t2 = {
      role: "ALL",
      statusKeys: t1,
      pageSize: PAGE_SIZE,
      page: 1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !router.isReady;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      variables: t2,
      fetchPolicy: "network-only",
      skip: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useQuery(ADVISOR_SESSIONS, t4);
  let t5;
  if ($[7] !== query.data?.advisorSessions?.items) {
    t5 = query.data?.advisorSessions?.items ?? [];
    $[7] = query.data?.advisorSessions?.items;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = t5.filter(_temp);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const firstPageItems = t6;
  const t7 = query.data?.advisorSessions?.pagination?.hasNextPage;
  let t8;
  if ($[11] !== bucket || $[12] !== firstPageItems || $[13] !== query.fetchMore || $[14] !== t7) {
    t8 = {
      fetchMore: query.fetchMore,
      field: "advisorSessions",
      firstPageItems,
      hasNextPage: t7,
      resetKey: bucket
    };
    $[11] = bucket;
    $[12] = firstPageItems;
    $[13] = query.fetchMore;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    items,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore(t8);
  let t9;
  if ($[16] !== paths) {
    t9 = acceptingSessionsDirectoryHref(paths);
    $[16] = paths;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const bookHref = t9;
  const activeItemTitle = bucket === "all" ? "My sessions" : SESSION_BUCKET_LABELS[bucket];
  const t10 = BUCKET_EMPTY[bucket];
  const t11 = query.loading && !query.data;
  let t12;
  if ($[18] !== bookHref || $[19] !== bucket || $[20] !== hasNextPage || $[21] !== items || $[22] !== loadMore || $[23] !== loadingMore || $[24] !== paths || $[25] !== router) {
    t12 = items.length === 0 ? <Card.Root variant="subtle"><Card.Body><VStack align="stretch" gap={3}><Text fontWeight="semibold">{BUCKET_EMPTY[bucket].title}</Text><Text color="colorPalette.fg/60" fontFamily="longform">{BUCKET_EMPTY[bucket].body}</Text>{BUCKET_EMPTY[bucket].cta && <Button asChild={true} alignSelf="flex-start" variant="outline"><NextLink href={bookHref}>{BUCKET_EMPTY[bucket].cta}</NextLink></Button>}</VStack></Card.Body></Card.Root> : <VStack align="stretch" gap={3}>{items.map(session_0 => {
        const id = session_0.advisorSessionId as string;
        const when = session_0.scheduledAt || session_0.requestedAt;
        const viewerRole = session_0.currentUserRole;
        const action = sessionListAction(session_0.statusKey, viewerRole, !!session_0.currentUserRating);
        const href = action === "join" ? `${paths.sessions}/${id}/room` : action === "rate" ? `${paths.sessions}/${id}?rate=1` : `${paths.sessions}/${id}`;
        return <Card.Root key={id} size="sm" variant="interactive" onClick={() => router.push(`${paths.sessions}/${id}`)}><Card.Body><HStack align="center" gap={4} wrap="wrap"><VStack align="stretch" flex={1} gap={1} minWidth="180px"><HStack gap={2} marginBottom={1} wrap="wrap"><LuVideo /><Text fontWeight="semibold">{counterpart(session_0)}</Text>{session_0.currentUserRole && <Badge variant="subtle">{session_0.currentUserRole === "EXPERT" ? "You are the expert" : "You are the founder"}</Badge>}</HStack><Text color="colorPalette.fg/80" fontSize="sm" fontWeight="medium">{session_0.reason || "Session"} · {session_0.approximateLengthMinutes ?? "\u2014"} min{session_0.estimatedCostUsd != null ? ` · ~${formatUsd(session_0.estimatedCostUsd)}` : ""}</Text><Text color="colorPalette.fg/50" fontSize="xs">{session_0.scheduledAt ? "Scheduled" : "Requested"} {formatWhen(when)}</Text></VStack><Badge colorPalette={sessionStatusPalette(session_0.statusKey)} variant="subtle">{session_0.status?.title || session_0.statusKey || "\u2014"}</Badge><Button asChild={true} size="sm" variant={action === "open" ? "outline" : "cta"} onClick={_temp2}><NextLink href={href}>{action === "join" ? <LuVideo /> : action === "confirm" ? <LuCalendarPlus /> : null}{SESSION_LIST_ACTION_LABEL[action]}</NextLink></Button></HStack></Card.Body></Card.Root>;
      })}<AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} /></VStack>;
    $[18] = bookHref;
    $[19] = bucket;
    $[20] = hasNextPage;
    $[21] = items;
    $[22] = loadMore;
    $[23] = loadingMore;
    $[24] = paths;
    $[25] = router;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== query.error || $[28] !== t11 || $[29] !== t12) {
    t13 = <AdvisorsQueryState error={query.error} loading={t11}>{t12}</AdvisorsQueryState>;
    $[27] = query.error;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== activeItemTitle || $[32] !== bucket || $[33] !== t10.body || $[34] !== t13) {
    t14 = <SessionsLayout activeKey={bucket} description={t10.body} title={activeItemTitle}>{t13}</SessionsLayout>;
    $[31] = activeItemTitle;
    $[32] = bucket;
    $[33] = t10.body;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};
function _temp(session) {
  return !!session?.advisorSessionId;
}
function _temp2(event) {
  return event.stopPropagation();
}